/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconPencil, IconPlus, IconMinus } from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import ToggleButton from "../../../components/ToggleButton";
import RaidoField from "../../../components/common/RaidoField";
import {
  useFetchTimeSlotData,
  useTimeSlotCreateMutation,
  useUpdateTimeSlot,
  useUpdateTimeSlotStatus,
} from "../../../apis/timeSlotApis";
import MessageModal from "../../../components/MessageModal";
import MultiSelectInputCheckbox from "../../../components/MultiSelectInputCheckbox";
import { Controller, FieldError, useForm } from "react-hook-form";
import Input from "../../../components/Input";
import toast from "react-hot-toast";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import { useLanguage } from "../../../context/LanguageContext";

type FormValues = {
  type: string;
  slotName: string;
  days: any;
  startTime: Date;
  endTime: Date;
};

type LocalSelectedValue = {
  id?: string;
  value: string;
  text: string;
  type?: string;
};

export default function TimeSlot() {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const { t } = useLanguage();

  const {
    data: timeSlotData,
    isLoading: timeSlotLoading,
    isFetching,
    error: timeSlotError,
    refetch: timeSlotRefetch,
  } = useFetchTimeSlotData(pageNo, limit, "");

  const { mutate: updateTimeSlotStatus, isPending: activePending } =
    useUpdateTimeSlotStatus();
  const [timeSlotItem, setTimeSlotItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const [status, setStatus] = useState<string>("");
  const [timeSlotArray, setTimeSlotArray] = useState<any>(timeSlotData);
  const { mutate, isPending } = useTimeSlotCreateMutation();
  const { mutate: updateTimeSlot, isPending: TimeSlotPending } =
    useUpdateTimeSlot();
  const [timeSlotId, setTimeSlotId] = useState<any>(null);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: "Driver", // ✅ default type set
      days: [],
      slotName: "",
    },
  });

  useEffect(() => {
    if (timeSlotData) {
      setTimeSlotArray(timeSlotData);
    }
  }, [timeSlotData]);

  const handleAddClick = () => {
    reset({
      type: "Driver",
      days: [],
      slotName: "",
    });
    setMode(mode === "add" ? null : "add");
  };

  const handleEditClick = (item: any) => {
    const convertedDays = item?.days.map((day: string) => ({
      text: day,
      value: day,
    }));
    setTimeSlotId(item?.id);
    setValue("days", convertedDays);
    setValue("type", item?.type);
    setValue("slotName", item?.slotName);
    setValue("endTime", item?.endTime);
    setValue("startTime", item?.startTime);
    setMode("edit");

    setTimeout(() => {
      // Primary: Try common dashboard content containers
      const selectors = [
        ".content",
        ".main-content",
        ".dashboard-content",
        "main",
        "[role='main']",
        ".overflow-y-auto",
        ".scrollable-content",
      ];

      let scrolled = false;
      for (const selector of selectors) {
        const el = document.querySelector(selector) as HTMLElement;
        if (el && el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          scrolled = true;
          break;
        }
      }

      // Fallback: window
      if (!scrolled) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
  };

  const handleToggleStatus = async (timeSlot: any) => {
    setTimeSlotItem(timeSlot);
    setStatus(timeSlot.status);
    setActionModal(true);
  };

  const handleUpdateStatus = async () => {
    const newStatus = timeSlotItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updatedTimeSlot = timeSlotArray.timeSlots.map((v: any) =>
      v.id === timeSlotItem?.id ? { ...v, status: newStatus } : v
    );

    setTimeSlotArray({ ...timeSlotArray, timeSlots: updatedTimeSlot });
    await updateTimeSlotStatus({ id: timeSlotItem?.id, status: newStatus });
    setActionModal(false);
  };

  const handleAddTimeSlot = (data: any) => {
    mutate(
      { ...data, days: watch("days").map((e: any) => e.value) },
      {
        onSuccess: () => {
          reset({ type: "Driver" });
          timeSlotRefetch();
          setMode(null);
          toast.success(t("Time slot created successfully."));
        },
        onError: (error) => {
          console.log(error);
          toast.error(`${error}`);
        },
      }
    );
  };

  const handleUpdateTimeSlot = async (data: FormValues) => {
    updateTimeSlot(
      {
        id: timeSlotId,
        type: watch("type"),
        slotName: watch("slotName"),
        startTime: watch("startTime"),
        endTime: watch("endTime"),
        days: watch("days").map((e: any) => e.value),
      },
      {
        onSuccess: () => {
          reset({ type: "Driver" });
          timeSlotRefetch();
          setMode(null);
          toast.success(t("Time slot updated successfully."));
        },
        onError: (error) => {
          console.log(error);
          toast.error(`${error}`);
        },
      }
    );
  };

  const handlePaginationFetch = (page: number) => setPageNo(page);

  return (
    <>
      <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]">
        <div className="flex justify-between items-center">
          <p className="text-lg font-medium text-[#212121]">{t("Time Slot Info")}</p>
          <button
            onClick={handleAddClick}
            className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
          >
            {mode === "add" || mode === "edit" ? (
              <IconMinus stroke={1.5} size={20} />
            ) : (
              <IconPlus stroke={1.5} size={20} />
            )}
          </button>
        </div>

        {(mode === "add" || mode === "edit") && (
          <div className="bg-[#FFFFFF] p-4 rounded-lg border border-[#D2D2D2]">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-[#212121]/70 text-base font-medium">
                  {mode === "add" ? t("Add Time Slot") : t("Edit Time Slot")}
                </h2>
                <RaidoField
                  label=""
                  value={watch("type")}
                  onClick={(val: any) => {
                    if (typeof val === "string") setValue("type", val);
                  }}
                  valuesArr={[
                    { value: "Driver", text: t("Driver") },
                    { value: "Staff", text: t("Staff") },
                  ]}
                />
              </div>

              <MultiSelectInputCheckbox
                className="flex justify-between items-center"
                labelClass="w-[40%]"
                label={t("Days")}
                name="days"
                selectOptions={[
                  { text: t("Monday"), value: "Monday" },
                  { text: t("Tuesday"), value: "Tuesday" },
                  { text: t("Wednesday"), value: "Wednesday" },
                  { text: t("Thursday"), value: "Thursday" },
                  { text: t("Friday"), value: "Friday" },
                  { text: t("Saturday"), value: "Saturday" },
                  { text: t("Sunday"), value: "Sunday" },
                ]}
                register={register}
                clearErrors={clearErrors}
                validation={{ required: t("At least one day is required") }}
                error={errors.days as FieldError | undefined}
                value={
                  Array.isArray(watch("days"))
                    ? (watch("days") as LocalSelectedValue[])
                    : []
                }
                setValue={(val: LocalSelectedValue[]) => {
                  setValue("days", val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                multiple={true}
              />

              <div className="flex justify-between items-center">
                <label className="text-[#1E293B] text-sm w-[40%]">
                  {t("Time Range")}
                </label>
                <div className="flex items-center w-full gap-2">
                  <Input
                    type="time"
                    name="startTime"
                    placeholder=""
                    className="w-full"
                    register={register}
                    error={errors.startTime}
                    validation={{ required: t("Start Time is required") }}
                  />
                  <Input
                    type="time"
                    name="endTime"
                    placeholder=""
                    className="w-full"
                    register={register}
                    error={errors.endTime}
                    validation={{ required: t("End Time is required") }}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <label className="text-[#1E293B] text-sm w-[40%]">
                  {t("Time Slot Name")}
                </label>
                <Input
                  name="slotName"
                  placeholder={t("Enter Time Slot Name")}
                  className="w-full"
                  register={register}
                  error={errors.slotName}
                  validation={{
                    required: t("Time Slot Name is required"),
                    pattern: {
                      value: /^(?=.*[A-Za-z])[A-Za-z0-9\s\-_/&.,()|]+$/,
                      message:
                        t("Time Slot Name must include at least one letter (not only numbers)"),
                    },
                    validate: (value) => {
                      if (!value?.trim())
                        return t("Time Slot Name cannot be empty or only spaces");
                      return true;
                    },
                  }}
                  type="text"
                />
              </div>

              <div className="mt-4">
                <button
                  disabled={mode === "add" ? isPending : TimeSlotPending}
                  onClick={handleSubmit((data) => {
                    if (mode === "add") {
                      handleAddTimeSlot(data);
                    } else if (mode === "edit") {
                      handleUpdateTimeSlot(data);
                    }
                  })}
                  className={`bg-[#FBD037] text-[#212121] text-sm font-semibold px-4 py-2 rounded-full ${
                    (mode === "add" ? isPending : TimeSlotPending)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {mode === "add" ? t("Save") : t("Update")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="-mx-4">
          {timeSlotLoading || isFetching ? (
            <TableSkeleton
              mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              subClassName=""
              columns={[
                "#",
                t("Name"),
                t("Days"),
                t("Range"),
                t("Creator"),
                t("For"),
                t("Status"),
                t("Actions"),
              ]}
              rows={6}
            />
          ) : timeSlotArray?.timeSlots?.length ? (
            <ListView
              mainDivClassName="bg-transparent"
              tdClassName="rounded-r-none rounded-l-none"
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent"
              thClassName="border-t"
              data={timeSlotArray?.timeSlots}
              columns={[
                {
                  label: "#",
                  render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
                },
                { label: t("Name"), render: (c: any) => c.slotName },
                {
                  label: t("Days"),
                  render: (c: any) => {
                    const dayOrder = [
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ];
                    const dayShortMap: Record<string, string> = {
                      Monday: t("Mon"),
                      Tuesday: t("Tue"),
                      Wednesday: t("Wed"),
                      Thursday: t("Thu"),
                      Friday: t("Fri"),
                      Saturday: t("Sat"),
                      Sunday: t("Sun"),
                    };
                    const sortedDays = [...c.days].sort(
                      (a: string, b: string) =>
                        dayOrder.indexOf(a) - dayOrder.indexOf(b)
                    );
                    return sortedDays
                      .map((day: string) => dayShortMap[day])
                      .join(", ");
                  },
                },
                {
                  label: t("Range"),
                  render: (c: any) => `${c.startTime} - ${c.endTime}`,
                },
                {
                  label: t("Creator"),
                  render: (c: any) => c.createdByName || "",
                },
                { label: t("For"), render: (c: any) => t(c.type) },
                {
                  label: t("Status"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <ToggleButton
                        onChange={() => handleToggleStatus(c)}
                        checked={c.status === "ACTIVE"}
                      />
                    </div>
                  ),
                },
                {
                  label: t("Actions"),
                  render: (c) => (
                    <div className="flex justify-center items-center">
                      <button
                        onClick={() => handleEditClick(c)}
                        className="p-2 border border-[#D2D2D2] rounded-full"
                      >
                        <IconPencil size={20} />
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <NoDataFound
              subClassName=""
              mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
            />
          )}
        </div>
      </div>
      {timeSlotArray?.totalPages > 1 && (
        <div className="flex justify-center items-center py-4">
          <Pagination
            currentPage={pageNo}
            totalPages={timeSlotArray?.totalPages}
            fetchData={handlePaginationFetch}
          />
        </div>
      )}

      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={status === "ACTIVE" ? "/img/inactive.svg" : "/img/active.svg"}
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={handleUpdateStatus}
        isPending={activePending}
        size="md"
      />
    </>
  );
}
